import { useEffect } from "react";
import { ThemeProvider as NextThemesProvider, useTheme } from "next-themes";
import { type ThemeProviderProps } from "next-themes";
import { useUserPreferences } from "./UserPreferencesProvider";

/**
 * Evo Workspace Theme Detection
 * 
 * This component listens for theme messages from the parent Evo workspace.
 * If Evo passes theme information, the app will automatically switch themes.
 * If no theme data is received, the app remains in light mode (default).
 * 
 * Message format expected from Evo:
 * window.postMessage({ type: 'evo-theme-change', theme: 'light' | 'dark' }, '*')
 */
function ThemeSyncWrapper({ children }: { children: React.ReactNode }) {
  const { theme, setTheme } = useTheme();
  const { preferences, updatePreferences } = useUserPreferences();

  // Sync theme from preferences on mount
  useEffect(() => {
    if (preferences.theme && theme !== preferences.theme) {
      setTheme(preferences.theme);
    }
  }, []);

  // Update preferences when theme changes
  useEffect(() => {
    if (theme && theme !== preferences.theme) {
      updatePreferences({ theme: theme as 'light' | 'dark' | 'system' });
    }
  }, [theme]);

  // Listen for theme changes from Evo workspace (future-proofing)
  useEffect(() => {
    const handleEvoThemeMessage = (event: MessageEvent) => {
      // Validate message structure
      if (
        event.data &&
        event.data.type === 'evo-theme-change' &&
        (event.data.theme === 'light' || event.data.theme === 'dark')
      ) {
        console.log('[Evo Theme] Received theme change from workspace:', event.data.theme);
        setTheme(event.data.theme);
      }
    };

    // Add event listener for postMessage from parent Evo workspace
    window.addEventListener('message', handleEvoThemeMessage);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('message', handleEvoThemeMessage);
    };
  }, [setTheme]);

  return <>{children}</>;
}

export function ThemeProvider({ children, ...props }: ThemeProviderProps) {
  return (
    <NextThemesProvider {...props}>
      <ThemeSyncWrapper>{children}</ThemeSyncWrapper>
    </NextThemesProvider>
  );
}
